import '../App.css';
import OrderSearchForm from "../ui-components/OrderSearchForm";
import CreditNoteForm from "../ui-components/CreditNoteForm";
import LineItemsDataGrid from "../components/LineItemsDataGrid";
import OrdersDataGrid from "../components/OrdersDataGrid";
import FileUploadDownload from "../components/FileUploadDownload";
import { Alert, Heading, Flex, Button } from "@aws-amplify/ui-react";
import { API, Auth, graphqlOperation } from "aws-amplify";
import { useState } from 'react';
import { CreditNote } from "../models";
import { v4 } from 'uuid';
import { useNavigate } from 'react-router-dom';
import { generateCreditNoteNumber } from '../utils/generateCreditNoteNumber';
import { formatAWSDate } from '../utils/formatAWSDate';
import * as mutations from '../graphql/mutations';

const getOrderQuery = `
  query GetOrder($order_number: String!) {
    getOrder(order_number: $order_number) {
      order_number
      customer_name
      delivery_date
      order_value
      brand
      brand_country
      line_items {
        sku
        item_name
        quantity
        gross_amount
      }
    }
  }
`;

export default function CreateCreditNote() {
  const [creditNote, setCreditNote] = useState();
  const [lineItems, setLineItems] = useState([]);
  const [order, setOrder] = useState();
  const [orders, setOrders] = useState();
  const [isSaving, setIsSaving] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [folder] = useState(v4());

  const navigate = useNavigate();

  const addAlert = (alertMessage) => {
    setAlerts(prevAlerts => [...prevAlerts,
    <Alert key={v4()} isDismissible={true} variation="error">{alertMessage}</Alert>
    ]);
  }

  const getOrder = async (
    order_number
  ) => {
    let response;
    setIsSearching(true);
    try {
      response = await API.graphql(graphqlOperation(getOrderQuery, { order_number }));
    } catch (error) {
      console.log(error);
      addAlert(error.errors[0].message);
      setIsSearching(false);
      return;
    }
    const order = response.data.getOrder;
    order.credit_note_number = await generateCreditNoteNumber(order.order_number);
    const creditNote = new CreditNote({
      id: v4(),
      credit_note_number: order.credit_note_number,
      order_number: order.order_number,
      customer_name: order.customer_name,
      delivery_date: order.delivery_date,
      order_value: order.order_value,
      credit_type: 'Credit',
      reason: '',
      comments: '',
      approved_by: '',
      requested_at: new Date().toISOString().substring(0, 10),
      brand: order.brand,
      country: order.brand_country,
      folder: folder
    });
    const lineItems = order.line_items.map(line_item => ({
      id: v4(),
      sku: line_item.sku,
      item_name: line_item.item_name,
      quantity_ordered: line_item.quantity,
      quantity_to_credit: null,
      original_unit_price: (line_item.quantity === 0 ? 0 : line_item.gross_amount / line_item.quantity).toFixed(2),
      unit_price: (line_item.quantity === 0 ? 0 : line_item.gross_amount / line_item.quantity).toFixed(2)
    }));
    setOrder(order);
    setOrders([order]);
    setCreditNote(creditNote);
    setLineItems(lineItems);
    setIsSearching(false);
    setAlerts([]);
  };

  const creditNoteValidation = () => {
    let validation_error = null;
    if (!creditNote.comments) {
      lineItems.forEach(async lineItem => {
        if (lineItem.unit_price > lineItem.original_unit_price) {
          validation_error = 'Reason comments must be entered if unit price is greater than original unit price';
        }
        if (lineItem.quantity_to_credit > lineItem.quantity_ordered) {
          validation_error = 'Reason comments must be entered if quantity to credit is greater than quantity ordered';
        }
      });
    }
    return validation_error;
  }

  const submitCreditNote = async () => {
    setIsSaving(true);
    try {
      // Validate credit note before submission
      const validation_error = creditNoteValidation();
      if (validation_error) {
        addAlert(validation_error);
        setIsSaving(false);
        return;
      }
      
      let approved_at = null;
      let status = 'Pending';
      if (creditNote.approved_by) {
        approved_at = new Date().toISOString();
        status = 'Approved';
      }
      
      const credit_note_number = await generateCreditNoteNumber(order.order_number);
      const currentUser = await Auth.currentUserInfo();
      if (!currentUser) {
        // not logged in, force a login
        window.location.reload();
        return;
      }
      
      // Create credit note first
      const response = await API.graphql(graphqlOperation(mutations.createCreditNote, { input: {
        credit_note_number: credit_note_number,
        order_number: order.order_number,
        customer_name: order.customer_name,
        delivery_date: order.delivery_date,
        order_value: order.order_value,
        credit_type: creditNote.credit_type,
        reason: creditNote.reason,
        comments: creditNote.comments,
        at_fault: creditNote.at_fault,
        status: status,
        approved_by: creditNote.approved_by,
        approved_at: approved_at,
        requested_at: creditNote.requested_at,
        brand: order.brand,
        country: order.brand_country,
        folder: folder,
        created_by: currentUser.attributes.given_name + ' ' + currentUser.attributes.family_name,
        created_at: new Date().toISOString(),
        updated_by: currentUser.attributes.given_name + ' ' + currentUser.attributes.family_name,
        updated_at: new Date().toISOString()
      }}));
      
      // Check for GraphQL errors in credit note creation
      if (response.errors && response.errors.length > 0) {
        throw new Error(`Credit note creation failed: ${response.errors[0].message}`);
      }
      
      const savedCreditNote = response.data.createCreditNote;
      
      // Create line items with proper error handling
      const lineItemPromises = lineItems.map(async (lineItem, index) => {
        try {
          // Validate line item data before sending
          if (!lineItem.sku || !lineItem.item_name) {
            throw new Error(`Missing required fields (SKU: ${lineItem.sku}, Item Name: ${lineItem.item_name})`);
          }
          
          // Validate numeric fields
          if (lineItem.quantity_ordered && isNaN(parseInt(lineItem.quantity_ordered))) {
            throw new Error(`Invalid quantity ordered: ${lineItem.quantity_ordered}`);
          }
          
          if (lineItem.quantity_to_credit && isNaN(parseInt(lineItem.quantity_to_credit))) {
            throw new Error(`Invalid quantity to credit: ${lineItem.quantity_to_credit}`);
          }
          
          // Validate and format the input data
          const lineItemInput = {
            id: v4(), // Generate a fresh ID for each submission attempt
            creditNoteLine_itemsId: savedCreditNote.id,
            sku: lineItem.sku,
            item_name: lineItem.item_name,
            quantity_ordered: lineItem.quantity_ordered ? parseInt(lineItem.quantity_ordered) : 0,
            quantity_to_credit: lineItem.quantity_to_credit ? parseInt(lineItem.quantity_to_credit) : 0,
            original_unit_price: lineItem.original_unit_price ? parseFloat(lineItem.original_unit_price) : 0,
            unit_price: lineItem.unit_price ? parseFloat(lineItem.unit_price) : 0
          };
          
          // Only include use_by_date if it has a valid value
          const formattedDate = formatAWSDate(lineItem.use_by_date);
          if (formattedDate) {
            lineItemInput.use_by_date = formattedDate;
          }
          
          const lineItemResponse = await API.graphql(graphqlOperation(mutations.createLineItem, { input: lineItemInput }));
          
          // Check for GraphQL errors in the response
          if (lineItemResponse.errors && lineItemResponse.errors.length > 0) {
            console.error('Line item creation GraphQL errors:', lineItemResponse.errors);
            console.error('Failed line item input:', lineItemInput);
            throw new Error(`Line item ${index + 1} (${lineItem.sku}): ${lineItemResponse.errors[0].message}`);
          }
          
          return lineItemResponse.data.createLineItem;
        } catch (error) {
          console.error(`Error creating line item ${index + 1}:`, error);
          console.error('Line item data:', lineItem);
          throw new Error(`Failed to create line item ${index + 1} (${lineItem.sku}): ${error.message}`);
        }
      });
      
      // Wait for all line items to be created
      try {
        await Promise.all(lineItemPromises);
        navigate('/');
      } catch (lineItemError) {
        // If line items fail, we need to clean up the credit note
        // Since this is a create form, we'll delete the partially created credit note
        try {
          await API.graphql(graphqlOperation(mutations.deleteCreditNote, { input: { id: savedCreditNote.id } }));
          addAlert(`Credit note creation failed: ${lineItemError.message}.`);
        } catch (deleteError) {
          addAlert(`Critical error: Credit note was created but line items failed, and cleanup also failed. Credit note ID: ${savedCreditNote.id}. Line item error: ${lineItemError.message}. Cleanup error: ${deleteError.message}`);
        }
        setIsSaving(false);
        return;
      }
    } catch (error) {
      console.log(error);
      addAlert(error.toString());
      setIsSaving(false);
      return;
    }
  }

  return (
    <Flex margin="0 auto" direction="column" gap="1rem">
      <Flex direction="column">
        <Heading level={2}>Create Credit Note</Heading>
        <div className="order-search-form">
          <OrderSearchForm
            onSubmit={async (fields) => {
              await getOrder(fields.order_number);
            }}
            onCancel={() => { navigate('/') }}
            overrides={{
              SubmitButton: {
                isLoading: isSearching,
                loadingText: "Searching..."
              }
            }}
          />
        </div>
        {alerts}
        {orders && <OrdersDataGrid orders={orders} lineItems={lineItems} />}
      </Flex>
      {creditNote && <Flex className="responsive-flex">
        <LineItemsDataGrid lineItems={lineItems} onChange={lineItems => setLineItems(lineItems)} />
        <Flex direction="column">
          <Button variation="primary" isLoading={isSaving} loadingText="Submitting..." onClick={() => submitCreditNote()}>
            Submit credit note
          </Button>
          <CreditNoteForm creditNote={creditNote} onChange={fields => setCreditNote(fields)} />
          <Heading level={4}>Attachments</Heading>
          <FileUploadDownload folder={folder} />
        </Flex>
      </Flex>}
    </Flex>
  );
}
